/**
 * useWebRTCAudioCall Hook
 * 
 * Custom React hook for managing WebRTC AUDIO-ONLY calls in ConnectieFlow.
 * Handles the full lifecycle of peer-to-peer audio calls using Firebase signaling.
 * 
 * CRITICAL: This is ONLY for audio calls. Video calls use Google Meet.
 * CRITICAL: All resources MUST be released when call ends (for Google Meet compatibility).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { webrtcService, WebRTCCall } from '../services/webrtcService';
import {
  createOrUpdateCallWithOffer,
  setAnswerOnCall,
  listenForAnswer,
  addIceCandidateToCall,
  listenForRemoteCandidates,
  cleanupCallDocument,
  getCallById
} from '../services/webrtcSignalingService';

interface UseWebRTCAudioCallProps {
  flowId: string;
  userId: string;
  otherUserId: string;
  otherUserName?: string;  // Name of the other user
  enabled: boolean;  // Only setup call when this is true
  isInitiator: boolean;  // True if this user proposed the call
}

interface UseWebRTCAudioCallReturn {
  call: WebRTCCall | null;
  isConnected: boolean;
  isMuted: boolean;
  error: string | null;
  toggleMute: () => void;
  endCall: () => void;
}

export const useWebRTCAudioCall = ({
  flowId,
  userId,
  otherUserId,
  otherUserName,
  enabled,
  isInitiator
}: UseWebRTCAudioCallProps): UseWebRTCAudioCallReturn => {
  
  const [call, setCall] = useState<WebRTCCall | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setupInProgressRef = useRef(false);
  const answerListenerUnsubscribeRef = useRef<(() => void) | null>(null);
  const candidatesListenerUnsubscribeRef = useRef<(() => void) | null>(null);

  // Setup WebRTC call when enabled
  useEffect(() => {
    if (!enabled || !flowId || !userId || !otherUserId) {
      console.log('🔄 [WebRTC Hook] Conditions not met, skipping setup:', {
        enabled,
        hasFlowId: !!flowId,
        hasUserId: !!userId,
        hasOtherUserId: !!otherUserId
      });
      return;
    }

    // Prevent duplicate setup
    if (setupInProgressRef.current) {
      console.log('🔄 [WebRTC Hook] Setup already in progress, skipping');
      return;
    }

    // Also check if already in a call
    if (webrtcService.isInCall()) {
      console.log('🔄 [WebRTC Hook] Already in a call, skipping setup');
      return;
    }

    setupInProgressRef.current = true;
    console.log(`🎤 [WebRTC Hook] Setting up audio call: ${isInitiator ? 'INITIATOR' : 'JOINER'}`);

    const setupCall = async () => {
      try {
        console.log('🎤 [WebRTC Hook] === SETUP CALL START ===');
        console.log('🎤 [WebRTC Hook] flowId:', flowId);
        console.log('🎤 [WebRTC Hook] userId:', userId);
        console.log('🎤 [WebRTC Hook] otherUserId:', otherUserId);
        console.log('🎤 [WebRTC Hook] isInitiator:', isInitiator);
        
        // Setup event handlers
        webrtcService.setCallHandlers({
          onCallStart: (newCall) => {
            console.log('✅ [WebRTC Hook] Call started:', newCall.id);
            setCall(newCall);
          },
          onCallEnd: (endedCall) => {
            console.log('✅ [WebRTC Hook] Call ended:', endedCall.id);
            setCall(null);
            setIsConnected(false);
          },
          onCallError: (err) => {
            console.error('❌ [WebRTC Hook] Call error:', err);
            setError(err.message);
          },
          onRemoteStream: (stream) => {
            console.log('🎤 [WebRTC Hook] Remote stream received!');
            console.log('🎤 [WebRTC Hook] Stream has', stream.getTracks().length, 'tracks');
            stream.getTracks().forEach(track => {
              console.log('🎤 [WebRTC Hook] Track:', track.kind, track.enabled, track.readyState);
            });
            setIsConnected(true);
          },
          onIceCandidate: async (candidate) => {
            console.log('🧊 [WebRTC Hook] Local ICE candidate generated');
            try {
              await addIceCandidateToCall(flowId, userId, candidate.toJSON());
              console.log('✅ [WebRTC Hook] ICE candidate saved to Firebase');
            } catch (err) {
              console.error('❌ [WebRTC Hook] Error saving ICE candidate:', err);
            }
          }
        });

        if (isInitiator) {
          // ==========================================
          // INITIATOR (Caller) Flow
          // ==========================================
          console.log('📞 [WebRTC Hook] Starting as INITIATOR...');

          // 1. Start local media and create peer connection
          const newCall = await webrtcService.initiateCall([userId, otherUserId]);
          // Add userName if available
          if (otherUserName) {
            newCall.userName = otherUserName;
          }
          setCall(newCall);

          // 2. Create offer
          const offer = await webrtcService.createOffer();

          // 3. Save offer to Firebase
          await createOrUpdateCallWithOffer(flowId, userId, otherUserId, offer);
          console.log('✅ [WebRTC Hook] Offer saved to Firebase');

          // 4. Listen for answer from callee
          const unsubscribeAnswer = listenForAnswer(flowId, async (answer) => {
            console.log('📞 [WebRTC Hook] Answer received from callee');
            try {
              await webrtcService.setRemoteDescription(answer);
              console.log('✅ [WebRTC Hook] Remote description set');
            } catch (err) {
              console.error('❌ [WebRTC Hook] Error setting remote description:', err);
              setError('Failed to connect');
            }
          });
          answerListenerUnsubscribeRef.current = unsubscribeAnswer;

          // 5. Listen for remote ICE candidates
          const unsubscribeCandidates = listenForRemoteCandidates(
            flowId,
            userId,
            async (candidate) => {
              console.log('🧊 [WebRTC Hook] Remote ICE candidate received');
              try {
                await webrtcService.addIceCandidate(candidate);
              } catch (err) {
                console.error('❌ [WebRTC Hook] Error adding remote ICE candidate:', err);
              }
            }
          );
          candidatesListenerUnsubscribeRef.current = unsubscribeCandidates;

        } else {
          // ==========================================
          // JOINER (Callee) Flow
          // ==========================================
          console.log('📞 [WebRTC Hook] Starting as JOINER...');

          // 1. Wait for offer from Firebase (with retries)
          console.log('⏳ [WebRTC Hook] Waiting for offer from initiator...');
          let callInfo = null;
          let retries = 0;
          const maxRetries = 30;
          const retryDelayMs = 700;
          
          while (retries < maxRetries) {
            callInfo = await getCallById(flowId);
            if (callInfo && callInfo.offer) {
              console.log('✅ [WebRTC Hook] Offer retrieved from Firebase after', retries, 'retries');
              break;
            }
            
            console.log('⏳ [WebRTC Hook] Offer not ready yet', {
              flowId,
              attempt: retries + 1,
              maxRetries,
              hasOffer: !!callInfo?.offer
            });
            await new Promise(resolve => setTimeout(resolve, retryDelayMs)); // Wait before next try
            retries++;
          }
          
          if (!callInfo || !callInfo.offer) {
            console.error('❌ [WebRTC Hook] Offer timeout details:', {
              flowId,
              userId,
              otherUserId,
              totalWaitTime: `${(maxRetries * retryDelayMs) / 1000}s`,
              isInitiator,
              suggestion: 'The initiator may not have started the call yet, or there may be a network issue.'
            });
            throw new Error(`No offer found in Firebase after ${maxRetries} retries (flowId=${flowId}). The initiator may not have started the call yet.`);
          }

          // 2. Start local media and create peer connection
          const newCall = await webrtcService.joinCall(flowId);
          // Add userName if available
          if (otherUserName) {
            newCall.userName = otherUserName;
          }
          setCall(newCall);

          // 3. Create answer (this also sets remote description internally)
          const answer = await webrtcService.createAnswer(callInfo.offer);

          // 4. Save answer to Firebase
          await setAnswerOnCall(flowId, answer);
          console.log('✅ [WebRTC Hook] Answer saved to Firebase');

          // 5. Listen for remote ICE candidates
          const unsubscribeCandidates = listenForRemoteCandidates(
            flowId,
            userId,
            async (candidate) => {
              console.log('🧊 [WebRTC Hook] Remote ICE candidate received');
              try {
                await webrtcService.addIceCandidate(candidate);
              } catch (err) {
                console.error('❌ [WebRTC Hook] Error adding remote ICE candidate:', err);
              }
            }
          );
          candidatesListenerUnsubscribeRef.current = unsubscribeCandidates;
        }

        setupInProgressRef.current = false;

      } catch (err: any) {
        console.error('❌ [WebRTC Hook] Setup error:', err);
        setError(err.message || 'Failed to setup call');
        setupInProgressRef.current = false;
        
        // Clean up on error
        webrtcService.endCall();
      }
    };

    setupCall();

    // Cleanup on unmount or when enabled changes
    return () => {
      console.log('🛑 [WebRTC Hook] Cleaning up...');
      
      // Unsubscribe from Firebase listeners
      if (answerListenerUnsubscribeRef.current) {
        answerListenerUnsubscribeRef.current();
        answerListenerUnsubscribeRef.current = null;
      }
      if (candidatesListenerUnsubscribeRef.current) {
        candidatesListenerUnsubscribeRef.current();
        candidatesListenerUnsubscribeRef.current = null;
      }

      // End WebRTC call and release all resources
      webrtcService.endCall();

      // Clean up Firebase call document
      cleanupCallDocument(flowId).catch(err => {
        console.error('❌ [WebRTC Hook] Error cleaning up call document:', err);
      });

      setupInProgressRef.current = false;
    };
  }, [enabled, flowId, userId, otherUserId, isInitiator]);

  // Toggle mute function
  const toggleMute = useCallback(() => {
    const localStream = webrtcService.getLocalStream();
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
        console.log(`🎤 [WebRTC Hook] Mute toggled: ${!audioTrack.enabled ? 'MUTED' : 'UNMUTED'}`);
      }
    }
  }, []);

  // End call function
  const endCall = useCallback(() => {
    console.log('📞 [WebRTC Hook] Ending call manually...');
    
    // Unsubscribe from listeners
    if (answerListenerUnsubscribeRef.current) {
      answerListenerUnsubscribeRef.current();
      answerListenerUnsubscribeRef.current = null;
    }
    if (candidatesListenerUnsubscribeRef.current) {
      candidatesListenerUnsubscribeRef.current();
      candidatesListenerUnsubscribeRef.current = null;
    }

    // End WebRTC call
    webrtcService.endCall();

    // Clean up Firebase
    cleanupCallDocument(flowId).catch(err => {
      console.error('❌ [WebRTC Hook] Error cleaning up call document:', err);
    });

    setCall(null);
    setIsConnected(false);
    setIsMuted(false);
  }, [flowId]);

  return {
    call,
    isConnected,
    isMuted,
    error,
    toggleMute,
    endCall
  };
};





